import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import {
    FaCar, FaArrowLeft, FaEdit, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt,
    FaCalendarAlt, FaShieldAlt, FaTachometerAlt, FaGasPump, FaCogs,
    FaUsers, FaCheck, FaExclamationTriangle, FaIdCard, FaCamera, FaFileAlt
} from 'react-icons/fa';
import '../../pages_premium.css';
import './CarView.css';
import { getFullImageUrl } from '../../utils/url';

/* Safe data helpers */
const safeParseJson = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val;
        if (typeof parsed === 'object') return parsed;
    } catch (e) {
        if (typeof val === 'string') return { info: val };
    }
    return null;
};

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val;
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'string') {
            const inner = JSON.parse(parsed);
            if (Array.isArray(inner)) return inner;
        }
    } catch (e) {
        if (typeof val === 'string') return [val];
    }
    return [];
};

const getImgUrl = (img) => {
    return getFullImageUrl(img);
};

const fmtDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatUserRole = (roleStr) => {
    if (!roleStr) return 'Lister';
    const r = roleStr.toLowerCase();
    if (r === 'agent' || r === 'lister') return 'Lister';
    if (r === 'owner' || r === 'business') return 'Business Owner';
    if (r === 'student') return 'Student';
    if (r === 'admin') return 'Admin';
    return 'Lister';
};

const getUserRoleBadgeClass = (roleStr) => {
    if (!roleStr) return 'badge-role-lister';
    const r = roleStr.toLowerCase();
    if (r === 'owner' || r === 'business') return 'badge-role-owner';
    if (r === 'student') return 'badge-role-student';
    if (r === 'admin') return 'badge-role-admin';
    return 'badge-role-lister';
};

function CarView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [car, setCar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [activeImgIdx, setActiveImgIdx] = useState(0);

    useEffect(() => {
        let mounted = true;
        const fetchCar = async () => {
            try {
                const res = await axiosInstance.get(`/cars/${id}`);
                if (mounted) {
                    setCar(res.data || null);
                    setNotFound(!res.data);
                }
            } catch (error) {
                console.error('Error fetching car details:', error);
                if (mounted) {
                    setNotFound(true);
                    toast.error('Failed to load car details');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchCar();
        return () => { mounted = false; };
    }, [id]);

    if (loading) {
        return (
            <div className="page-premium-wrapper car-view-page">
                <div className="page-premium-content">
                    <div className="cv-center-state">
                        <div className="cv-spinner"></div>
                        <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Loading car listing details…</span>
                    </div>
                </div>
            </div>
        );
    }

    if (notFound || !car) {
        return (
            <div className="page-premium-wrapper car-view-page">
                <div className="page-premium-content">
                    <div className="cv-center-state">
                        <div style={{ fontSize: '3rem', color: '#ef4444' }}><FaExclamationTriangle /></div>
                        <h4 style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>Car listing not found</h4>
                        <p style={{ margin: 0, fontSize: '0.88rem', color: '#64748b' }}>The requested car listing does not exist or has been removed.</p>
                        <button className="cv-back-btn mt-2" onClick={() => navigate('/cars')}>
                            <FaArrowLeft /> Back to Cars Management
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* Derived data */
    const images = safeParseArray(car.images);
    const features = safeParseArray(car.features);
    const verif = safeParseJson(car.verification);
    const isSelling = car.listing_type === 'selling' || car.listing_type === 'sale' || car.type === 'selling';
    const isAvailable = car.status === 'available';
    const owner = car.User || {};
    const rawRole = car.listing_role || owner.role;
    const roleLabel = formatUserRole(rawRole);
    const roleBadgeClass = getUserRoleBadgeClass(rawRole);

    return (
        <div className="page-premium-wrapper car-view-page">
            <div className="page-premium-content">

                {/* ---------- Hero Header ---------- */}
                <div className="page-hero-header">
                    <div className="cv-hero">
                        <div className="cv-hero-left">
                            <div className="cv-hero-icon">
                                <FaCar />
                            </div>
                            <div>
                                <h1 className="ph-title">{car.title || `${car.make} ${car.model}`}</h1>
                                <p className="ph-subtitle">
                                    {car.location || car.city || 'Location not specified'}
                                    {' · '}
                                    <span className={`badge ${roleBadgeClass} px-2 py-0.5 ml-1 text-capitalize`} style={{ fontSize: '11px' }}>
                                        {roleLabel}
                                    </span>
                                    {' · '}
                                    <span className={`badge ${isSelling ? 'badge-type-selling' : 'badge-type-rental'} px-2 py-0.5 ml-1 text-capitalize`} style={{ fontSize: '11px' }}>
                                        {isSelling ? '🏷️ Selling' : '🚗 Rental'}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div className="cv-hero-right">
                            <span className={`cv-status ${isAvailable ? 'cv-status-active' : 'cv-status-inactive'}`}>
                                <span className="cv-dot"></span>
                                {isAvailable ? 'Available' : (car.status || 'Inactive')}
                            </span>
                            <Link to={`/cars/edit/${car.id}`} className="cv-btn-edit">
                                <FaEdit /> Edit Listing
                            </Link>
                        </div>
                    </div>
                </div>

                {/* ---------- Back Button ---------- */}
                <div style={{ marginBottom: '18px' }}>
                    <button className="cv-back-btn" onClick={() => navigate('/cars')}>
                        <FaArrowLeft /> Back to Cars Management
                    </button>
                </div>

                {/* ---------- Content Grid ---------- */}
                <div className="cv-grid">
                    {/* Main Column */}
                    <div className="cv-col-main">

                        {/* Photo Gallery Card */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span>
                                <FaCamera className="cv-title-icon" /> Vehicle Gallery ({images.length})
                            </h3>

                            {images.length > 0 ? (
                                <>
                                    <div className="cv-gallery-main">
                                        <img src={getImgUrl(images[activeImgIdx])} alt={car.title} />
                                        {images.length > 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="cv-gallery-nav cv-gallery-prev"
                                                    onClick={() => setActiveImgIdx(p => (p - 1 + images.length) % images.length)}
                                                >
                                                    &lsaquo;
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cv-gallery-nav cv-gallery-next"
                                                    onClick={() => setActiveImgIdx(p => (p + 1) % images.length)}
                                                >
                                                    &rsaquo;
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {images.length > 1 && (
                                        <div className="cv-thumbs">
                                            {images.map((img, idx) => (
                                                <button
                                                    type="button"
                                                    key={idx}
                                                    className={`cv-thumb-btn ${activeImgIdx === idx ? 'active' : ''}`}
                                                    onClick={() => setActiveImgIdx(idx)}
                                                >
                                                    <img src={getImgUrl(img)} alt={`thumb ${idx + 1}`} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="cv-gallery-main d-flex flex-column align-items-center justify-content-center text-muted">
                                    <FaCar size={48} className="mb-2 opacity-50" />
                                    <span style={{ fontSize: '0.9rem' }}>No vehicle images uploaded</span>
                                </div>
                            )}
                        </div>

                        {/* Vehicle Specifications */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span>
                                <FaCogs className="cv-title-icon" /> Vehicle Specifications
                            </h3>

                            <div className="cv-spec-grid">
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaCogs /> Transmission</span>
                                    <span className="cv-spec-val">{car.transmission || 'Automatic'}</span>
                                </div>
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaGasPump /> Fuel Type</span>
                                    <span className="cv-spec-val">{car.fuelType || 'Petrol'}</span>
                                </div>
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaUsers /> Seating</span>
                                    <span className="cv-spec-val">{car.seats ? `${car.seats} Seats` : '5 Seats'}</span>
                                </div>
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaCalendarAlt /> Model Year</span>
                                    <span className="cv-spec-val">{car.year || 'N/A'}</span>
                                </div>
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaTachometerAlt /> Mileage Limit</span>
                                    <span className="cv-spec-val">{car.mileageLimit || 'Unlimited'}</span>
                                </div>
                                <div className="cv-spec-box">
                                    <span className="cv-spec-lbl"><FaShieldAlt /> Insurance</span>
                                    <span className="cv-spec-val">{car.insuranceIncluded ? 'Included' : 'Not Included'}</span>
                                </div>
                            </div>

                            <div className="mt-3 pt-3 border-top">
                                <div className="row">
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted small">Make:</span> <strong>{car.make || 'N/A'}</strong>
                                    </div>
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted small">Model / Variant:</span> <strong>{car.model || ''} {car.variant ? `(${car.variant})` : ''}</strong>
                                    </div>
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted small">Body Type:</span> <strong>{car.type || 'Sedan'}</strong>
                                    </div>
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted small">Listing Type:</span> <strong className="text-capitalize">{isSelling ? 'For Sale' : 'Rental'}</strong>
                                    </div>
                                </div>
                            </div>

                            {features.length > 0 && (
                                <div className="mt-3 pt-3 border-top">
                                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', marginBottom: '10px' }}>Features & Amenities</h4>
                                    <div className="cv-chips">
                                        {features.map((f, i) => (
                                            <span key={i} className="cv-chip cv-chip-gray">
                                                <FaCheck className="text-success" style={{ fontSize: '11px' }} /> {f}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Verification & Documents */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span>
                                <FaShieldAlt className="cv-title-icon" /> Verification & Documents
                            </h3>

                            {verif ? (
                                <div className="cv-verif-list">
                                    <div className="cv-verif-item">
                                        <span className="cv-verif-k">Registration (Rego) Number</span>
                                        <span className="cv-verif-v">{verif.regoNumber || 'N/A'}</span>
                                    </div>
                                    <div className="cv-verif-item">
                                        <span className="cv-verif-k">Rego State & Expiry</span>
                                        <span className="cv-verif-v">
                                            {verif.regoState || 'N/A'} {verif.regoExpiry ? `(Exp: ${fmtDate(verif.regoExpiry)})` : ''}
                                        </span>
                                    </div>
                                    <div className="cv-verif-item">
                                        <span className="cv-verif-k">VIN / Chassis Number</span>
                                        <span className="cv-verif-v">{verif.vin || 'N/A'}</span>
                                    </div>
                                    <div className="cv-verif-item">
                                        <span className="cv-verif-k">Insurance Provider</span>
                                        <span className="cv-verif-v">{verif.insuranceProvider || 'N/A'}</span>
                                    </div>
                                    <div className="cv-verif-item">
                                        <span className="cv-verif-k">Insurance Expiry</span>
                                        <span className="cv-verif-v">{verif.insuranceExpiry ? fmtDate(verif.insuranceExpiry) : 'N/A'}</span>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: 0 }}>No verification details provided for this vehicle.</p>
                            )}
                        </div>

                        {/* Description & Overview */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span>
                                <FaFileAlt className="cv-title-icon" /> Overview & Description
                            </h3>

                            {car.shortDesc && (
                                <p style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>
                                    {car.shortDesc}
                                </p>
                            )}

                            {car.description ? (
                                <div className="cv-desc">{car.description}</div>
                            ) : (
                                <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: 0 }}>No detailed description provided.</p>
                            )}
                        </div>
                    </div>

                    {/* Sidebar Column */}
                    <div className="cv-col-side">

                        {/* Pricing & Status */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span> Pricing & Status
                            </h3>

                            <div className="mb-3">
                                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
                                    {isSelling ? 'Sale Price' : 'Rental Rate'}
                                </span>
                                <div className="cv-price-tag">
                                    ${car.price}
                                    <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                                        {isSelling ? ' (Sale)' : ` / ${car.period || 'day'}`}
                                    </span>
                                </div>
                            </div>

                            <div className="cv-facts">
                                <div className="cv-fact">
                                    <span className="cv-fact-label">Listing Type</span>
                                    <span className={`badge ${isSelling ? 'badge-type-selling' : 'badge-type-rental'} px-2 py-0.5 text-capitalize`}>
                                        {isSelling ? 'Selling' : 'Rental'}
                                    </span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label">Availability Status</span>
                                    <span className={`cv-status ${isAvailable ? 'cv-status-active' : 'cv-status-inactive'}`} style={{ padding: '2px 10px' }}>
                                        {car.status || 'Available'}
                                    </span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label">Insurance Included</span>
                                    <span className="cv-fact-value">{car.insuranceIncluded ? 'Yes' : 'No'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Listed Owner Card */}
                        <div className="cv-card cv-user-card">
                            <div className="cv-user-avatar">
                                {owner.name ? owner.name.charAt(0).toUpperCase() : 'L'}
                            </div>
                            <h3 className="cv-user-name">{car.contactName || owner.name || 'Vehicle Lister'}</h3>
                            <span className={`badge ${roleBadgeClass} px-3 py-1 mb-2 text-capitalize`} style={{ fontSize: '12px' }}>
                                {roleLabel}
                            </span>

                            <div className="cv-contact-list">
                                {(car.email || owner.email) && (
                                    <div className="cv-contact-item">
                                        <FaEnvelope className="cv-contact-icon" />
                                        <span className="text-truncate">{car.email || owner.email}</span>
                                    </div>
                                )}
                                {(car.phone || owner.phone) && (
                                    <div className="cv-contact-item">
                                        <FaPhoneAlt className="cv-contact-icon" />
                                        <span>{car.phone || owner.phone}</span>
                                    </div>
                                )}
                                {(car.location || car.street || car.city) && (
                                    <div className="cv-contact-item">
                                        <FaMapMarkerAlt className="cv-contact-icon" />
                                        <span>{[car.street, car.city, car.state, car.postcode].filter(Boolean).join(', ') || car.location}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Facts */}
                        <div className="cv-card">
                            <h3 className="cv-card-title">
                                <span className="cv-title-bar"></span> Quick Facts
                            </h3>

                            <div className="cv-facts">
                                <div className="cv-fact">
                                    <span className="cv-fact-label"><FaIdCard className="cv-fact-icon" /> Car ID</span>
                                    <span className="cv-fact-value">#{car.id}</span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label"><FaUsers className="cv-fact-icon" /> Owner User ID</span>
                                    <span className="cv-fact-value">{owner.id || car.owner_id ? `#${owner.id || car.owner_id}` : '—'}</span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label"><FaCalendarAlt className="cv-fact-icon" /> Listed Date</span>
                                    <span className="cv-fact-value">{fmtDate(car.createdAt)}</span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label"><FaCalendarAlt className="cv-fact-icon" /> Last Updated</span>
                                    <span className="cv-fact-value">{fmtDate(car.updatedAt)}</span>
                                </div>
                                <div className="cv-fact">
                                    <span className="cv-fact-label"><FaTachometerAlt className="cv-fact-icon" /> Mileage Limit</span>
                                    <span className="cv-fact-value">{car.mileageLimit || 'Unlimited'}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
}

export default CarView;
